import { QueryClient, type QueryClientConfig } from '@tanstack/react-query';

import { ApiError } from './errors';

/**
 * Retry policy.
 *
 * Only transport-level failures are worth retrying: a 403 or a business-rule
 * 400 will fail identically no matter how many times we ask. Retrying those
 * just delays the error the user needs to see.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (!error.isRetryable) return false;
    return failureCount < 2;
  }
  return failureCount < 1;
}

const config: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Domain data (charges, buildings, members) changes on human timescales.
      // A minute of freshness removes most redundant refetching while keeping
      // navigation between screens instant.
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      // Mobile browsers fire `focus` on every app-switch; refetching on mount
      // and reconnect covers staleness without the churn.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      throwOnError: false,
    },
    mutations: {
      // Mutations are user-initiated and often non-idempotent (payments!).
      // Never retry them automatically.
      retry: false,
    },
  },
};

export const queryClient = new QueryClient(config);

export { queryKeys } from './query-keys';
